/**
 * ReportsPageNew — full feature parity with old ReportsPage.
 * Includes: trip selector, per-trip visit data, all-time top attractions grouped by park with bars, search filter.
 */
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTripVisitReports, useTripAttractionReports } from '../hooks/useReports';
import { useTrips } from '../hooks/useTrips';
import { DataCard } from '../design-system/primitives/DataCard';
// EmptyState import — reserved for future use
// SkeletonRow import — reserved for future use
import { SearchInput } from '../components/shared/SearchInput';
import { BarChart3, Star, Calendar, ArrowRight, MapPin, ListChecks } from 'lucide-react';

const PARK_COLORS: Record<string, { bar: string; bg: string; badge: string }> = {
  'Magic Kingdom': { bar: 'bg-purple-500 dark:bg-purple-400', bg: 'bg-purple-50 dark:bg-purple-950/30', badge: 'text-purple-600 dark:text-purple-400' },
  'Epcot': { bar: 'bg-blue-500 dark:bg-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/30', badge: 'text-blue-600 dark:text-blue-400' },
  'EPCOT': { bar: 'bg-blue-500 dark:bg-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/30', badge: 'text-blue-600 dark:text-blue-400' },
  "Disney's Hollywood Studios": { bar: 'bg-amber-500 dark:bg-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30', badge: 'text-amber-600 dark:text-amber-400' },
  'Animal Kingdom': { bar: 'bg-green-500 dark:bg-green-400', bg: 'bg-green-50 dark:bg-green-950/30', badge: 'text-green-600 dark:text-green-400' },
};

function getParkStyles(parkName: string) {
  return PARK_COLORS[parkName] ?? { bar: 'bg-gray-400', bg: 'bg-gray-50', badge: 'text-gray-600 dark:text-gray-400' };
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function ReportsPageNew(): React.ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _navigate = useNavigate();
  const { data: trips } = useTrips();
  const { data: tripReports } = useTripVisitReports();
  const { data: attractionReports } = useTripAttractionReports();
  const [selectedTripId, setSelectedTripId] = React.useState('');
  const [searchFilter, setSearchFilter] = React.useState('');

  const tripVisits = tripReports?.filter((r) => r.tripId === selectedTripId) ?? [];
  const tripAttractions = attractionReports?.find((r) => r.tripId === selectedTripId)?.attractions ?? [];
  const selectedTrip = trips?.find((t) => t.id === selectedTripId);

  const totalTrips = tripReports?.length ?? 0;
  const totalVisits = tripReports?.reduce((sum, r) => sum + r.visitCount, 0) ?? 0;
  const avgVisits = totalTrips > 0 ? (totalVisits / totalTrips).toFixed(1) : '0';

  const allTimeAttractions = React.useMemo(() => {
    const source = selectedTripId
      ? (attractionReports ?? []).filter((r) => r.tripId === selectedTripId)
      : (attractionReports ?? []);
    const map = new Map<string, { name: string; park: string; total: number }>();
    for (const report of source) {
      for (const item of report.attractions) {
        const existing = map.get(item.attractionId);
        if (existing) existing.total += item.count;
        else map.set(item.attractionId, { name: item.attractionName, park: item.parkName, total: item.count });
      }
    }
    return Array.from(map.entries()).sort(([, a], [, b]) => b.total - a.total);
  }, [attractionReports, selectedTripId]);

  const filteredTrips = React.useMemo(() => {
    if (!searchFilter) return tripReports ?? [];
    return tripReports?.filter((r) =>
      r.tripName.toLowerCase().includes(searchFilter.toLowerCase()) ||
      r.visitCount.toString().includes(searchFilter)
    ) ?? [];
  }, [tripReports, searchFilter]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-disney-blue-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Reports</h1>
        </div>
        {trips && trips.length > 0 && (
          <select value={selectedTripId} onChange={(e) => setSelectedTripId(e.target.value)}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-disney-blue-500 focus:outline-none focus:ring-1 focus:ring-disney-blue-500"
            aria-label="Select trip">
            <option value="">All trips</option>
            {trips.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>

      {/* Summary cards — thin stat row as spec says */}
      <div className="grid grid-cols-3 gap-3">
        <DataCard title={selectedTrip ? `1 of ${totalTrips}` : totalTrips.toString()} subtitle={selectedTrip ? selectedTrip.name : `${totalTrips} total`} />
        <DataCard title={selectedTripId ? tripVisits.reduce((sum, r) => sum + r.visitCount, 0).toString() : totalVisits.toString()} subtitle="Total Park Visits" />
        <DataCard title={selectedTripId ? tripVisits.length > 0 ? (tripVisits.reduce((sum, r) => sum + r.visitCount, 0) / Math.max(tripVisits.length, 1)).toFixed(1) : '0' : avgVisits} subtitle="Avg Visits per Trip" />
      </div>

      {/* Search */}
      <SearchInput value={searchFilter} onChange={setSearchFilter} onClear={() => setSearchFilter('')} placeholder="Filter by name or park..." />

      {/* Park visits per trip */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-disney-blue-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Park Visits per Trip</h3>
        </div>
        {selectedTripId ? (
          tripVisits.length > 0 ? (
            <div className="px-4 py-3">
              <Link to={`/admin/reports/${selectedTripId}/calendar`}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-disney-blue-600 dark:text-disney-blue-400 hover:text-disney-blue-700 dark:hover:text-disney-blue-300 hover:underline">
                <Calendar className="w-4 h-4" />
                <span>View calendar ({tripVisits.reduce((sum, r) => sum + r.visitCount, 0)} visits)</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">No trips with visits yet</div>
          )
        ) : tripReports && tripReports.length > 0 ? (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredTrips.map((report) => (
              <div key={report.tripId} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{report.tripName}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{formatDate(report.startDate)} to {formatDate(report.endDate)}</p>
                </div>
                <span className="text-2xl font-bold text-disney-blue-500 dark:text-disney-blue-400">{report.visitCount}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">No trips with visits yet</div>
        )}
      </div>

      {/* Attraction selections per trip — only shown when a trip is selected */}
      {selectedTripId && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Attraction Selections</h3>
          </div>
          {tripAttractions.length > 0 ? (
            <div className="px-4 py-3">
              <Link to={`/admin/reports/${selectedTripId}/attractions`}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-disney-blue-600 dark:text-disney-blue-400 hover:text-disney-blue-700 dark:hover:text-disney-blue-300 hover:underline">
                <ListChecks className="w-4 h-4" />
                <span>View selections ({tripAttractions.length} attractions)</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">No attractions selected for this trip</div>
          )}
        </div>
      )}

      {/* All-time top attractions — grouped by park, with horizontal bars */}
      {allTimeAttractions.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-4 h-4 text-amber-500" />
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{selectedTripId ? 'Top attractions for this trip' : 'All-time top attractions'}</p>
          </div>
          {(() => {
            const groups: Map<string, typeof allTimeAttractions> = new Map();
            const shown = searchFilter
              ? allTimeAttractions.filter(([, a]) =>
                  a.name.toLowerCase().includes(searchFilter.toLowerCase()) || a.park.toLowerCase().includes(searchFilter.toLowerCase()))
              : allTimeAttractions;
            const maxCount = Math.max(...allTimeAttractions.map(([, a]) => a.total), 1);
            shown.forEach(([, item]) => {
              const existing = groups.get(item.park) ?? [];
              existing.push([item.name, { name: item.name, park: item.park, total: item.total }]);
              groups.set(item.park, existing);
            });
            return Array.from(groups.entries()).slice(0, 8).map(([park, items]) => {
              const styles = getParkStyles(park);
              return (
                <div key={park} className="mb-3 last:mb-0">
                  <p className={`text-xs font-semibold mb-1 ${styles.badge} uppercase tracking-wide`}>{park}</p>
                  <div className="space-y-1">
                    {items.map(([, item]) => {
                      const pct = Math.max((item.total / maxCount) * 100, 6);
                      return (
                        <div key={item.name} className="flex items-center gap-2">
                          <span className="w-40 text-xs text-gray-700 dark:text-gray-300 truncate shrink-0" title={item.name}>{item.name}</span>
                          <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${styles.bar} transition-all duration-300`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-8 text-right text-xs font-bold text-gray-600 dark:text-gray-400">{item.total}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}
